import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle } from "lucide-react";

/**
 * Themed replacement for window.confirm and window.prompt.
 *
 * <ConfirmDialog
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Ukloniti komentar?"
 *   description="Ova akcija se ne može vratiti."
 *   confirmLabel="Ukloni"
 *   destructive
 *   onConfirm={() => …}                       // simple confirm
 * />
 *
 * <ConfirmDialog
 *   … requireReason
 *   reasonLabel="Razlog uklanjanja"
 *   reasonPlaceholder="Zašto uklanjaš?"
 *   onConfirm={(reason) => …}
 * />
 */
export default function ConfirmDialog({
  open,
  onOpenChange,
  title = "Da li si sigurna?",
  description,
  confirmLabel = "Potvrdi",
  cancelLabel = "Otkaži",
  destructive = false,
  requireReason = false,
  optionalReason = false,        // reason is optional but text area still shown
  reasonLabel = "Razlog",
  reasonPlaceholder = "",
  onConfirm,
  testidPrefix = "confirm-dialog",
}) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);

  const canConfirm = !requireReason || reason.trim().length > 0;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const arg = (requireReason || optionalReason) ? reason.trim() : undefined;
      await onConfirm?.(arg);
      onOpenChange?.(false);
    } catch {
      // errors surface via toast in caller
    }
    setSubmitting(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        data-testid={testidPrefix}
        className="max-w-sm sm:max-w-md rounded-lg border border-line"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="text-base font-semibold text-ink flex items-center gap-2">
            {destructive && <AlertTriangle className="w-4 h-4 text-terracotta" />}
            {title}
          </AlertDialogTitle>
          {description && (
            <AlertDialogDescription className="text-sm text-ink-muted">
              {description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>

        {(requireReason || optionalReason) && (
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-ink-muted font-medium">
              {reasonLabel}{requireReason && <span className="text-terracotta"> *</span>}
            </label>
            <Textarea
              autoFocus
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
              className="text-sm resize-none"
              data-testid={`${testidPrefix}-reason`}
            />
          </div>
        )}

        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel
            data-testid={`${testidPrefix}-cancel`}
            className="text-sm border border-line bg-white hover:bg-surface-secondary text-ink"
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={`${testidPrefix}-confirm`}
            disabled={!canConfirm || submitting}
            onClick={(e) => { e.preventDefault(); handleConfirm(); }}
            className={
              "text-sm " +
              (destructive
                ? "bg-terracotta text-white hover:bg-terracotta/90"
                : "bg-forest text-white hover:bg-forest/90") +
              ((!canConfirm || submitting) ? " opacity-60 cursor-not-allowed" : "")
            }
          >
            {submitting ? "…" : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
